import { create } from "zustand";
import { env } from "@/lib/env";
import { User } from "../types/user";

type AuthSnapshot = {
  user: User | null;
};

interface AuthState extends AuthSnapshot {
  isHydrated: boolean;
  initialize: () => Promise<void>;
  setAuth: (payload: { user: User }) => void;
  clearAuth: () => void;
}

type SessionResponse = {
  user: User;
};

let initializePromise: Promise<void> | null = null;

const emptySnapshot = (): AuthSnapshot => ({
  user: null,
});

const buildAuthUrl = (path: string) => {
  const normalizedBase = env.apiUrl.replace(/\/+$/, "");
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${normalizedBase}${normalizedPath}`;
};

const parseSessionResponse = async (
  response: Response,
): Promise<SessionResponse | null> => {
  if (!response.ok) {
    return null;
  }

  const data = (await response.json()) as Partial<SessionResponse>;
  return data.user ? { user: data.user } : null;
};

const resolveSession = async (): Promise<AuthSnapshot> => {
  if (typeof window === "undefined") {
    return emptySnapshot();
  }

  try {
    const meResponse = await fetch(buildAuthUrl("/auth/me"), {
      method: "GET",
      credentials: "include",
      headers: {
        Accept: "application/json",
      },
    });

    const meData = await parseSessionResponse(meResponse);
    if (meData?.user) {
      return { user: meData.user };
    }

    if (meResponse.status !== 401) {
      return emptySnapshot();
    }

    const refreshResponse = await fetch(buildAuthUrl("/auth/refresh"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({}),
    });

    const refreshData = await parseSessionResponse(refreshResponse);
    if (refreshData?.user) {
      return { user: refreshData.user };
    }

    return emptySnapshot();
  } catch {
    return emptySnapshot();
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  isHydrated: false,
  initialize: async () => {
    if (get().isHydrated) {
      return;
    }

    if (!initializePromise) {
      initializePromise = resolveSession()
        .then((snapshot) => {
          set({ ...snapshot, isHydrated: true });
        })
        .finally(() => {
          initializePromise = null;
        });
    }

    await initializePromise;
  },
  setAuth: ({ user }) => {
    set({ user, isHydrated: true });
  },
  clearAuth: () => {
    set({ user: null, isHydrated: true });
  },
}));
